import React, { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Download, Lock, Pencil, Plus, Search, Trash2, UserX, X } from 'lucide-react';
import { adminGetUsers, adminCreateUser, adminUpdateUser, adminDeleteUser, adminResetUserPassword, AdminUser } from '../../utils/remote';
import { useSortableTable, SortColumn } from '../../school_panel/lib/useSortableTable';
import { Button, ConfirmDialog, EmptyState, ErrorPanel, FormField, IconButton, Initials, LoadingState, Modal, PagedFooter, PageHeader, PanelCard, PillTabs, Segmented, Switch, Table, Td, Th, Tabs } from '../../school_panel/components/ui';
import { ago, lastSeen } from '../../school_panel/pages/dashboards/sections/format';
import { RoleBadge } from './adminUi';
import { downloadCsv, toCsv } from '../../school_panel/lib/csv';
import { fieldClass } from '../../school_panel/lib/fieldClass';
import { useAuth } from '../../utils/Auth/AuthProvider';
import { storeRotatedSession } from '../../utils/Auth/rotatedSession';

// Shared by the "name" column's sort accessor and the Name cell so a blank-name
// account is never sorted by one string while displaying another.
export function displayName(u: Pick<AdminUser, 'first_name' | 'last_name' | 'username'>): string {
  return `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim() || u.username;
}

const PAGE_SIZE = 10;

const USER_COLUMNS: SortColumn<AdminUser>[] = [
  { key: 'id', accessor: (u) => u.id, type: 'number' },
  { key: 'name', accessor: displayName },
  { key: 'joined', accessor: (u) => u.date_joined, type: 'date' },
  { key: 'last_exam', accessor: (u) => u.last_result_at, type: 'date' },
  { key: 'last_login', accessor: (u) => u.last_login, type: 'date' },
];

type TabKey = 'all' | 'superusers' | 'admins' | 'teachers' | 'students' | 'counselors' | 'inactive';

const TAB_TESTS: Record<TabKey, (u: AdminUser) => boolean> = {
  all: () => true,
  superusers: (u) => !!u.is_superuser,
  admins: (u) => u.role === 'school_admin' || u.role === 'admin',
  teachers: (u) => u.role === 'teacher',
  students: (u) => u.role === 'student',
  counselors: (u) => u.role === 'counselor',
  inactive: (u) => !u.is_active,
};

const TAB_LABELS: [TabKey, string][] = [
  ['all', 'All'], ['superusers', 'Superusers'], ['admins', 'School admins'], ['teachers', 'Teachers'],
  ['students', 'Students'], ['counselors', 'Counselors'], ['inactive', 'Inactive'],
];

const SORT_OPTIONS = [
  { value: 'name-asc', label: 'Name A→Z', key: 'name', dir: 'asc' },
  { value: 'name-desc', label: 'Name Z→A', key: 'name', dir: 'desc' },
  { value: 'joined-desc', label: 'Newest first', key: 'joined', dir: 'desc' },
  { value: 'last_login-desc', label: 'Last sign-in', key: 'last_login', dir: 'desc' },
  { value: 'last_exam-desc', label: 'Last exam', key: 'last_exam', dir: 'desc' },
] as const;

const errText = (e: any) => (e?.response?.data ? JSON.stringify(e.response.data) : e?.message ?? 'Unknown error');

function exportCsv(rows: AdminUser[]) {
  const head = ['id', 'username', 'name', 'email', 'role', 'school', 'active', 'superuser', 'date_joined', 'last_exam'];
  downloadCsv('yada-users.csv', toCsv(head, rows.map((u) => [
    u.id, u.username, displayName(u), u.email, u.role, u.institution_name, u.is_active, u.is_superuser, u.date_joined, u.last_result_at,
  ])));
}

const INPUT = fieldClass;

const EMPTY_FORM = { username: '', password: '', email: '', first_name: '', last_name: '', is_superuser: false, is_active: true };

type Pending = { ids: number[]; label: string; kind: 'delete' | 'deactivate' } | null;
type BulkJob = { ids: number[]; verb: string; run: (id: number) => Promise<unknown> };

const AdminUsers: React.FC = () => {
  const qc = useQueryClient();
  // The signed-in admin can never delete or deactivate their own account here.
  const { username: me, setToken } = useAuth();
  // Resolved to a user id once the list first loads, so renaming yourself can't bypass the guard.
  const [selfId, setSelfId] = useState<number | null>(null);
  const isSelf = (u: Pick<AdminUser, 'id' | 'username'>) => (selfId !== null ? u.id === selfId : !!me && u.username === me);
  const [tab, setTab] = useState<TabKey>('all');
  const [schoolFilter, setSchoolFilter] = useState<number | null>(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [pendingDelete, setPendingDelete] = useState<Pending>(null);
  const [rowError, setRowError] = useState<string | null>(null);
  // A single-row error is transient: it fades after 6s, or on the next action.
  useEffect(() => {
    if (!rowError) return;
    const t = setTimeout(() => setRowError(null), 6000);
    return () => clearTimeout(t);
  }, [rowError]);
  const [bulkError, setBulkError] = useState<string | null>(null);
  // Reset password: confirm first, then show the temporary password once. It lives only in
  // this state (never the query cache, console, storage or CSV) and is dropped on close.
  const [resetTarget, setResetTarget] = useState<AdminUser | null>(null);
  const [resetBusy, setResetBusy] = useState(false);
  const [revealed, setRevealed] = useState<{ name: string; staff: boolean; password: string } | null>(null);
  const [copied, setCopied] = useState(false);

  // The API is unpaginated and ignores `search`, so filtering and paging happen here.
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['admin-users'],
    queryFn: () => adminGetUsers({ page: 1, page_size: 100000 }),
    staleTime: 5_000,
  });
  const users = useMemo(() => data?.results ?? [], [data]);
  useEffect(() => {
    if (selfId !== null || !me) return;
    const row = users.find((u) => u.username === me);
    if (row) setSelfId(row.id);
  }, [users, me, selfId]);

  const refresh = () => qc.invalidateQueries({ queryKey: ['admin-users'] });

  const createMut = useMutation({
    mutationFn: adminCreateUser,
    onSuccess: () => { refresh(); setAddOpen(false); setForm(EMPTY_FORM); },
  });
  const updateMut = useMutation({
    mutationFn: ({ id, patch }: { id: number; patch: Parameters<typeof adminUpdateUser>[1] }) => adminUpdateUser(id, patch),
    // Changing your own password revokes your tokens; the response carries a fresh pair.
    // Store it before the refetch so the session carries on.
    // Only the caller's own password change may swap the session.
    onSuccess: (data, vars) => {
      const target = users.find((u) => u.id === vars.id);
      if (vars.patch.password && target && isSelf(target)) storeRotatedSession(data, setToken);
      refresh();
      setEditingUser(null);
    },
  });
  const bulkMut = useMutation({
    mutationFn: async (job: BulkJob) => {
      const res = await Promise.allSettled(job.ids.map((id) => job.run(id)));
      const failedIds = job.ids.filter((_, i) => res[i].status === 'rejected');
      return { ok: job.ids.length - failedIds.length, failedIds, verb: job.verb };
    },
    onSuccess: ({ ok, failedIds, verb }) => {
      refresh();
      // Failed rows stay selected so the admin can retry them.
      setSelected(new Set(failedIds));
      setPendingDelete(null);
      setBulkError(failedIds.length ? `${ok} ${verb} · ${failedIds.length} failed` : null);
    },
  });

  const [editingUser, setEditingUser] = useState<AdminUser | null>(null);
  const [editForm, setEditForm] = useState({ username: '', email: '', first_name: '', last_name: '', is_active: true, is_superuser: false, password: '' });

  const openEdit = (u: AdminUser) => {
    setRowError(null);
    setEditingUser(u);
    setEditForm({
      username: u.username, email: u.email || '', first_name: u.first_name || '', last_name: u.last_name || '',
      is_active: !!u.is_active, is_superuser: !!u.is_superuser, password: '',
    });
  };

  const submitEdit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingUser) return;
    const patch: Parameters<typeof adminUpdateUser>[1] = {
      username: editForm.username.trim() || editingUser.username,
      email: editForm.email.trim(),
      first_name: editForm.first_name.trim(),
      last_name: editForm.last_name.trim(),
      is_active: editForm.is_active,
      is_superuser: editForm.is_superuser,
    };
    if (editForm.password.length >= 6) patch.password = editForm.password;
    updateMut.mutate({ id: editingUser.id, patch });
  };

  const confirmReset = async () => {
    if (!resetTarget || resetBusy) return;
    const target = resetTarget;
    setResetBusy(true);
    setRowError(null);
    try {
      const res = await adminResetUserPassword(target.id);
      setRevealed({ name: displayName(target), staff: target.role !== 'student', password: res.temporary_password });
    } catch (e: any) {
      setRowError(e?.response?.status === 403
        ? 'Only a superuser can reset passwords.'
        : `Could not reset ${displayName(target)}'s password: ${errText(e)}`);
    } finally {
      setResetBusy(false);
      setResetTarget(null);
    }
  };
  const closeReveal = () => { setRevealed(null); setCopied(false); };
  const copyPassword = async () => {
    if (!revealed) return;
    try { await navigator.clipboard.writeText(revealed.password); setCopied(true); } catch { setCopied(false); }
  };

  const onCreate = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.username.trim()) return;
    createMut.mutate({
      username: form.username.trim(),
      password: form.password || undefined,
      email: form.email.trim() || undefined,
      first_name: form.first_name.trim() || undefined,
      last_name: form.last_name.trim() || undefined,
      is_superuser: form.is_superuser,
      is_active: form.is_active,
    });
  };

  const counts = useMemo(() => {
    const c = {} as Record<TabKey, number>;
    (Object.keys(TAB_TESTS) as TabKey[]).forEach((k) => { c[k] = users.filter(TAB_TESTS[k]).length; });
    return c;
  }, [users]);
  const schools = useMemo(() => new Set(users.map((u) => u.institution_id).filter((i) => i !== null && i !== undefined)).size, [users]);
  const schoolOptions = useMemo(() => {
    const m = new Map<number, string>();
    users.forEach((u) => { if (u.institution_id != null && u.institution_name) m.set(u.institution_id, u.institution_name); });
    return Array.from(m, ([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [users]);

  const { rows: sortedUsers, sort, toggle, setSort } = useSortableTable(users, USER_COLUMNS, { key: 'name', dir: 'asc' });

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return sortedUsers.filter((u) => TAB_TESTS[tab](u) && (schoolFilter === null || u.institution_id === schoolFilter) && (!q
      || u.username.toLowerCase().includes(q) || (u.email ?? '').toLowerCase().includes(q) || displayName(u).toLowerCase().includes(q)));
  }, [sortedUsers, tab, search, schoolFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const curPage = Math.min(page, totalPages);
  const pageRows = filtered.slice((curPage - 1) * PAGE_SIZE, curPage * PAGE_SIZE);

  // A user-initiated view change goes back to page 1 and drops the selection, so
  // bulk actions can't reach rows the admin can't see. A refetch that merely
  // clamps the page (curPage) does not, so failed rows stay selected for a retry.
  const viewChanged = () => { setPage(1); setSelected(new Set()); setRowError(null); };
  const changeTab = (t: TabKey) => { setTab(t); viewChanged(); };
  const changeSchool = (v: number | null) => { setSchoolFilter(v); viewChanged(); };
  const changeSearch = (v: string) => { setSearch(v); viewChanged(); };
  const toggleSort = (key: string) => { toggle(key); viewChanged(); };
  const pickSort = (o: { key: string; dir: 'asc' | 'desc' }) => { setSort({ key: o.key, dir: o.dir }); viewChanged(); };
  const goToPage = (n: number) => { setPage(n); setSelected(new Set()); setRowError(null); };

  const sortValue = SORT_OPTIONS.find((o) => o.key === sort?.key && o.dir === sort?.dir)?.value ?? '';
  const selectable = pageRows.filter((u) => !isSelf(u));
  const hasSelfOnPage = pageRows.some(isSelf);
  const allOnPage = selectable.length > 0 && selectable.every((u) => selected.has(u.id));
  const toggleOne = (id: number) => setSelected((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const togglePage = () => setSelected((s) => {
    const n = new Set(s);
    selectable.forEach((u) => (allOnPage ? n.delete(u.id) : n.add(u.id)));
    return n;
  });
  const selIds = selectable.filter((u) => selected.has(u.id)).map((u) => u.id);

  const countLabel = (n: number) => `${n} ${n === 1 ? 'user' : 'users'}`;
  // Activating only restores access, so it runs straight away; deactivating asks first.
  const activateSelected = () => {
    setBulkError(null);
    setRowError(null);
    bulkMut.mutate({ ids: selIds, verb: 'activated', run: (id) => adminUpdateUser(id, { is_active: true }) });
  };
  const confirmPending = () => {
    if (!pendingDelete) return;
    // Re-filter at confirm time: only rows still visible, never the signed-in admin.
    const visible = new Set(selectable.map((u) => u.id));
    const ids = pendingDelete.ids.filter((id) => visible.has(id));
    if (ids.length === 0) { setPendingDelete(null); return; }
    setBulkError(null);
    setRowError(null);
    if (pendingDelete.kind === 'deactivate') {
      bulkMut.mutate({ ids, verb: 'deactivated', run: (id) => adminUpdateUser(id, { is_active: false }) });
    } else {
      bulkMut.mutate({ ids, verb: 'deleted', run: adminDeleteUser });
    }
  };

  const sortBtn = (key: string, label: string) => (
    <button type="button" onClick={() => toggleSort(key)}
      className="inline-flex items-center gap-1 bg-transparent p-0 font-semibold uppercase text-inherit hover:text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-accent">
      <span>{label}</span>
      <span aria-hidden="true" className={sort?.key === key ? 'text-ink' : 'text-muted/50'}>{sort?.key === key ? (sort.dir === 'asc' ? '▲' : '▼') : '↕'}</span>
    </button>
  );
  const ariaSort = (key: string) => (sort?.key === key ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none');

  const subtitle = data
    ? `${users.length.toLocaleString('en-US')} accounts across ${schools} schools · ${users.filter((u) => u.is_active).length.toLocaleString('en-US')} active · ${counts.superusers} superusers.`
    : undefined;

  return (
    <div>
      <PageHeader
        eyebrow="Platform admin · Users"
        title="Users"
        subtitle={subtitle}
        actions={(
          <>
            <Button variant="ghost" icon={<Download size={16} aria-hidden />} disabled={filtered.length === 0} onClick={() => exportCsv(filtered)}>Export CSV</Button>
            <Button icon={<Plus size={16} aria-hidden />} onClick={() => setAddOpen(true)}>Add user</Button>
          </>
        )}
      />

      <PanelCard>
        <Tabs
          label="Filter by role"
          value={tab}
          onChange={changeTab}
          items={TAB_LABELS.map(([key, label]) => ({ key, label, count: counts[key] }))}
        />

        {schoolOptions.length > 0 && (
          <div className="mb-4 max-w-full">
            <PillTabs scroll dense label="Filter by school" value={schoolFilter === null ? 'all' : String(schoolFilter)}
              onChange={(k) => changeSchool(k === 'all' ? null : Number(k))}
              options={[{ key: 'all', label: 'All schools' }, ...schoolOptions.map((s) => ({ key: String(s.id), label: s.name }))]} />
          </div>
        )}
        <div className="mb-4 flex flex-wrap items-center gap-2.5">
          <label className="relative block max-w-[420px] flex-[1_1_280px]">
            <span className="sr-only">Search users</span>
            <Search size={16} aria-hidden className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-muted" />
            <input type="search" value={search} onChange={(e) => changeSearch(e.target.value)} placeholder="Username, email, or name"
              aria-label="Search users" className={`${INPUT} !min-h-[40px] py-0 pl-10`} />
          </label>
          <div className="flex min-w-0 max-w-full items-center gap-2 text-[13px] text-muted">
            <span aria-hidden="true">Sort</span>
            {/* A column-header sort outside these presets leaves no pill pressed. */}
            <Segmented dense scroll label="Sort users" value={sortValue}
              onChange={(v) => { const o = SORT_OPTIONS.find((x) => x.value === v); if (o) pickSort(o); }}
              options={SORT_OPTIONS.map((o) => ({ key: o.value, label: o.label }))} />
          </div>
          <span className="ml-auto text-[13px] text-muted">Showing {pageRows.length} of {filtered.length}</span>
        </div>

        {selIds.length > 0 && (
          <div role="region" aria-label="Bulk actions" className="mb-[14px] flex flex-wrap items-center gap-2.5 rounded-[14px] bg-navy py-2.5 pl-4 pr-2.5 text-on-navy">
            <span className="font-semibold">{selIds.length} selected</span>
            <span className="flex-1" />
            <Button variant="ghost" size="sm" disabled={bulkMut.isPending} onClick={activateSelected}>Activate</Button>
            <Button variant="ghost" size="sm" disabled={bulkMut.isPending} onClick={() => setPendingDelete({ ids: selIds, label: countLabel(selIds.length), kind: 'deactivate' })}>Deactivate</Button>
            <Button variant="ghost" size="sm" className="!text-danger-ink" disabled={bulkMut.isPending} onClick={() => setPendingDelete({ ids: selIds, label: countLabel(selIds.length), kind: 'delete' })}>Delete</Button>
            <button type="button" aria-label="Clear selection" onClick={() => setSelected(new Set())}
              className="grid h-[34px] w-[34px] place-items-center rounded-[10px] border border-navy-2 bg-navy-2 p-0 text-white hover:bg-navy-3"><X size={16} aria-hidden /></button>
          </div>
        )}
        {hasSelfOnPage && <p className="mb-3 text-[13px] text-muted">Your own account can't be selected, deactivated or deleted here.</p>}
        {bulkError && <p role="alert" className="mb-3 text-sm text-danger-ink">{bulkError}</p>}
        {rowError && <p role="alert" className="mb-3 text-sm text-danger-ink">{rowError}</p>}

        <div>
          {isLoading ? (
            <LoadingState label="Loading users…" />
          ) : isError ? (
            <ErrorPanel title="Failed to load" body="The users could not be loaded." onRetry={() => refetch()} />
          ) : filtered.length === 0 ? (
            <EmptyState icon={<UserX size={20} />} title="No users in this view" body="Try another role tab or clear the search." />
          ) : (
            <Table minWidth={900}>
              <thead>
                <tr>
                  <Th className="w-8">
                    <input type="checkbox" aria-label="Select all on this page" checked={allOnPage} onChange={togglePage} className="h-4 w-4 accent-navy" />
                  </Th>
                  <Th aria-sort={ariaSort('name')}>
                    <span className="inline-flex items-center gap-3">{sortBtn('name', 'Name')}{sortBtn('id', 'ID')}</span>
                  </Th>
                  <Th>Email</Th>
                  <Th>Role</Th>
                  <Th>School</Th>
                  <Th>Status</Th>
                  <Th aria-sort={ariaSort('last_login')}>{sortBtn('last_login', 'Last sign-in')}</Th>
                  <Th align="right">Actions</Th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map((u) => {
                  const name = displayName(u);
                  const on = selected.has(u.id);
                  const self = isSelf(u);
                  return (
                    <tr key={u.id} className={on ? '[&>td]:bg-surface-2' : ''}>
                      <Td>
                        <input type="checkbox" aria-label={`Select ${name}`} checked={on} disabled={self} onChange={() => toggleOne(u.id)} className="h-4 w-4 accent-navy" />
                      </Td>
                      <Td>
                        <span className="flex items-center gap-3">
                          <Initials name={name} />
                          <span className="min-w-0">
                            <span data-testid="user-name" className="block font-semibold text-ink">{name}</span>
                            {self && <span className="block text-xs font-semibold text-muted">You</span>}
                            <span className="block font-mono text-xs text-muted">@<span data-testid="user-username">{u.username}</span> · #{u.id}</span>
                          </span>
                        </span>
                      </Td>
                      <Td className="text-muted">{u.email}</Td>
                      <Td><RoleBadge role={u.role} isSuperuser={u.is_superuser} /></Td>
                      <Td className="text-ink">{u.institution_name || '—'}</Td>
                      <Td>
                        <span className="inline-flex items-center gap-2">
                          <Switch checked={!!u.is_active} label={`Active: ${name}`} disabled={self}
                            onChange={(v) => {
                              setRowError(null);
                              updateMut.mutate({ id: u.id, patch: { is_active: v } }, {
                                onError: (e) => setRowError(`Could not update ${name}: ${errText(e)}`),
                              });
                            }} />
                          <span className={`text-[13px] font-semibold ${u.is_active ? 'text-success' : 'text-muted'}`}>{u.is_active ? 'Active' : 'Inactive'}</span>
                        </span>
                      </Td>
                      <Td className="whitespace-nowrap">
                        <span data-testid="user-last-signin" className="block font-mono font-medium text-muted">{lastSeen(u.last_login)}</span>
                        <span className="block text-xs text-muted">Last exam: {u.last_result_at ? ago(u.last_result_at) : '—'}</span>
                      </Td>
                      <Td>
                        <span className="flex justify-end gap-1.5">
                          <IconButton label={`Edit ${name}`} onClick={() => openEdit(u)} icon={<Pencil size={16} aria-hidden />} />
                          {!u.is_superuser && !self && (
                            <IconButton label={`Reset password for ${name}`} onClick={() => { setRowError(null); setResetTarget(u); }} icon={<Lock size={16} aria-hidden />} />
                          )}
                          <IconButton label={`Delete ${name}`} tone="danger" disabled={self} onClick={() => setPendingDelete({ ids: [u.id], label: name, kind: 'delete' })} icon={<Trash2 size={16} aria-hidden />} />
                        </span>
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}
        </div>

        {!isLoading && !isError && filtered.length > 0 && (
          <PagedFooter divider="list" from={(curPage - 1) * PAGE_SIZE + 1} to={(curPage - 1) * PAGE_SIZE + pageRows.length}
            total={filtered.length} page={curPage} pageCount={totalPages} onPage={goToPage} label="Users pagination" />
        )}
      </PanelCard>

      <Modal
        isOpen={addOpen}
        onClose={() => setAddOpen(false)}
        eyebrow="New account"
        title="Add user"
        subtitle="Set a temporary password and share it privately."
        side="right"
        footer={(
          <>
            <Button variant="ghost" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button type="submit" form="add-user-form" loading={createMut.isPending}>Create user</Button>
          </>
        )}
      >
        <form id="add-user-form" onSubmit={onCreate} className="space-y-4">
          <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2">
            <FormField label="First name" htmlFor="add-first-name">
              <input id="add-first-name" placeholder="e.g. Tigist" className={INPUT} value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
            </FormField>
            <FormField label="Last name" htmlFor="add-last-name">
              <input id="add-last-name" placeholder="e.g. Alemu" className={INPUT} value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
            </FormField>
          </div>
          <FormField label="Username" htmlFor="add-username" required>
            <input id="add-username" placeholder="e.g. tigist.a" className={INPUT} value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} />
          </FormField>
          <FormField label="Email" htmlFor="add-email">
            <input id="add-email" type="email" placeholder="name@school.edu.et" className={INPUT} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </FormField>
          <FormField label="Temporary password" htmlFor="add-password" hint="Leave blank to create the account without a password.">
            <input id="add-password" type="password" placeholder="Min 6 characters" className={INPUT} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
          </FormField>
          <div className="flex flex-col gap-3 rounded-[14px] bg-surface-2 p-3.5">
            <div className="flex items-center gap-3">
              <Switch checked={form.is_superuser} label="Superuser" onChange={(v) => setForm({ ...form, is_superuser: v })} />
              <span><span className="block font-semibold text-ink">Superuser</span><span className="text-xs text-muted">Full platform access, all schools</span></span>
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={form.is_active} label="Active" onChange={(v) => setForm({ ...form, is_active: v })} />
              <span><span className="block font-semibold text-ink">Active</span><span className="text-xs text-muted">Inactive users cannot sign in</span></span>
            </div>
          </div>
          {createMut.isError && <p role="alert" className="text-sm text-danger-ink">Failed: {errText(createMut.error)}</p>}
        </form>
      </Modal>

      <Modal
        isOpen={!!editingUser}
        onClose={() => setEditingUser(null)}
        title={editingUser ? `Edit user: ${editingUser.username}` : 'Edit user'}
        size="lg"
        footer={(
          <>
            <Button variant="ghost" onClick={() => setEditingUser(null)}>Cancel</Button>
            <Button type="submit" form="edit-user-form" loading={updateMut.isPending}>Save changes</Button>
          </>
        )}
      >
        <form id="edit-user-form" onSubmit={submitEdit} className="space-y-4">
          <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2">
            <FormField label="Username" htmlFor="edit-username">
              <input id="edit-username" className={INPUT} value={editForm.username} onChange={(e) => setEditForm({ ...editForm, username: e.target.value })} />
            </FormField>
            <FormField label="Email" htmlFor="edit-email">
              <input id="edit-email" type="email" className={INPUT} value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} />
            </FormField>
            <FormField label="First name" htmlFor="edit-first-name">
              <input id="edit-first-name" className={INPUT} value={editForm.first_name} onChange={(e) => setEditForm({ ...editForm, first_name: e.target.value })} />
            </FormField>
            <FormField label="Last name" htmlFor="edit-last-name">
              <input id="edit-last-name" className={INPUT} value={editForm.last_name} onChange={(e) => setEditForm({ ...editForm, last_name: e.target.value })} />
            </FormField>
            <FormField label="New password (optional)" htmlFor="edit-password" hint="Leave blank to keep current. Min 6 characters if provided.">
              <input id="edit-password" type="password" className={INPUT} value={editForm.password} onChange={(e) => setEditForm({ ...editForm, password: e.target.value })} />
            </FormField>
          </div>
          <div className="flex flex-col gap-3 rounded-[14px] bg-surface-2 p-3.5">
            <div className="flex items-center gap-3">
              <Switch checked={editForm.is_active} label="Active" disabled={!!editingUser && isSelf(editingUser)} onChange={(v) => setEditForm({ ...editForm, is_active: v })} />
              <span className="font-semibold text-ink">Active{editingUser && isSelf(editingUser) ? ' (your own account stays active)' : ''}</span>
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={editForm.is_superuser} label="Superuser" onChange={(v) => setEditForm({ ...editForm, is_superuser: v })} />
              <span className="font-semibold text-ink">Superuser</span>
            </div>
          </div>
          {updateMut.isError && <p role="alert" className="text-sm text-danger-ink">Failed to update: {errText(updateMut.error)}</p>}
        </form>
      </Modal>

      <ConfirmDialog
        isOpen={!!resetTarget}
        title={`Reset ${resetTarget ? displayName(resetTarget) : ''}'s password?`}
        body="Their current password stops working and they are signed out everywhere. You will see a temporary password once."
        confirmLabel="Reset password"
        danger={false}
        busy={resetBusy}
        onConfirm={confirmReset}
        onCancel={() => setResetTarget(null)}
      />

      <Modal
        isOpen={!!revealed}
        onClose={closeReveal}
        title="Temporary password"
        size="sm"
        closeOnBackdrop={false}
        footer={<Button onClick={closeReveal}>Done</Button>}
      >
        {revealed && (
          <div className="space-y-3">
            <p className="text-sm text-muted">New password for <span className="font-semibold text-ink">{revealed.name}</span>. It is shown only once.</p>
            <div className="flex items-center gap-2">
              <code data-testid="temp-password" className="flex-1 select-all break-all rounded-[10px] bg-surface-2 px-3 py-2 font-mono text-base text-ink">{revealed.password}</code>
              <Button variant="ghost" size="sm" onClick={copyPassword}>{copied ? 'Copied' : 'Copy'}</Button>
            </div>
            <p className="text-sm text-muted">{revealed.staff ? "They'll be asked to set a new one at next sign-in." : 'Share it securely.'}</p>
          </div>
        )}
      </Modal>

      <ConfirmDialog
        isOpen={!!pendingDelete}
        title={pendingDelete?.kind === 'deactivate' ? `Deactivate ${pendingDelete.label}?` : `Delete ${pendingDelete?.label ?? ''}?`}
        body={pendingDelete?.kind === 'deactivate'
          ? 'They will no longer be able to sign in. You can reactivate them here.'
          : 'This permanently removes the account and cannot be undone.'}
        confirmLabel={pendingDelete?.kind === 'deactivate' ? 'Deactivate' : 'Delete'}
        busy={bulkMut.isPending}
        onConfirm={confirmPending}
        onCancel={() => setPendingDelete(null)}
      />
    </div>
  );
};

export default AdminUsers;
